import { useState, useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import UnifiedOnboarding from "@/components/UnifiedOnboarding";
import AddToHomeScreen from "@/components/AddToHomeScreen";
import SentryErrorBoundary from "@/components/SentryErrorBoundary";
import PwaUpdatePrompt from "@/components/PwaUpdatePrompt";
import LoginPage from "./pages/LoginPage";
import LandingPage from "./pages/LandingPage";
import NoGroupsPage from "./pages/NoGroupsPage";
import GroupPage from "./pages/GroupPage";
import BetPlacement from "./pages/BetPlacement";
import LeaderboardDrillIn from "./pages/LeaderboardDrillIn";
import SeriesDrillIn from "./pages/SeriesDrillIn";
import MyBetsDrillIn from "./pages/MyBetsDrillIn";
import SideBetsDrillIn from "./pages/SideBetsDrillIn";
import ManageDrillIn from "./pages/ManageDrillIn";
import HowItWorks from "./pages/HowItWorks";
import TipJar from "./pages/TipJar";
import About from "./pages/About";
import FaqPage from "./pages/FaqPage";
import FeedbackPage from "./pages/FeedbackPage";
import JoinGroup from "./pages/JoinGroup";
import BracketPage from "./pages/BracketPage";
import BracketComparePage from "./pages/BracketComparePage";
import NotFound from "./pages/NotFound";
import GlobalLeaderboardPage from "./pages/GlobalLeaderboardPage";
import AdminAnalytics from "./pages/AdminAnalytics";
import AdminPage from "./pages/AdminPage";
import { fetchUserGroups } from "@/lib/supabase-queries";
import { FEEDBACK_ENABLED, OFF_SEASON } from "@/lib/feature-flags";
import { useLastActiveGroup } from "@/hooks/useLastActiveGroup";
import { useDevMode } from "@/hooks/useDevMode";
import { Loader2 } from "lucide-react";

const queryClient = new QueryClient();

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  if (OFF_SEASON) return <Navigate to="/" replace />;
  const { user, loading, needsOnboarding } = useAuth();
  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-accent" /></div>;
  if (!user) return <LoginPage />;
  if (needsOnboarding) return <UnifiedOnboarding />;
  return <>{children}</>;
};

const PublicOrProtected = ({ children }: { children: React.ReactNode }) => {
  const { user, loading, needsOnboarding } = useAuth();
  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-accent" /></div>;
  if (!user) {
    const currentPath = window.location.pathname;
    if (currentPath.startsWith('/join/')) {
      localStorage.setItem('pendingInvitePath', currentPath);
    }
    return <LoginPage />;
  }
  if (needsOnboarding) return <UnifiedOnboarding />;
  return <>{children}</>;
};

const HomeRoute = () => {
  if (OFF_SEASON) return <LandingPage />;
  const { user, loading, needsOnboarding } = useAuth();
  const { lastGroupId } = useLastActiveGroup();
  const [groups, setGroups] = useState<any[] | null>(null);

  useEffect(() => {
    if (!user || needsOnboarding) return;
    fetchUserGroups(user.id).then(g => setGroups(g));
  }, [user, needsOnboarding]);

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-accent" /></div>;
  if (!user) return <LandingPage />;
  if (needsOnboarding) return <UnifiedOnboarding />;
  if (groups === null) return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-accent" /></div>;
  if (groups.length === 0) return <NoGroupsPage />;
  if (lastGroupId && groups.some(g => g.id === lastGroupId)) {
    return <Navigate to={`/group/${lastGroupId}`} replace />;
  }
  // No lastGroupId or stale — redirect to first group
  return <Navigate to={`/group/${groups[0].id}`} replace />;
};

const AppRoutes = () => (
  <Routes>
    <Route path="/" element={<HomeRoute />} />
    <Route path="/login" element={<LoginPage />} />
    <Route path="/group/:groupId" element={<ProtectedRoute><GroupPage /></ProtectedRoute>} />
    <Route path="/group/:groupId/series/:seriesId" element={<ProtectedRoute><BetPlacement /></ProtectedRoute>} />
    <Route path="/group/:groupId/leaderboard" element={<ProtectedRoute><LeaderboardDrillIn /></ProtectedRoute>} />
    <Route path="/group/:groupId/series" element={<ProtectedRoute><SeriesDrillIn /></ProtectedRoute>} />
    <Route path="/group/:groupId/bets" element={<ProtectedRoute><MyBetsDrillIn /></ProtectedRoute>} />
    <Route path="/group/:groupId/bets/:userId" element={<ProtectedRoute><MyBetsDrillIn /></ProtectedRoute>} />
    <Route path="/group/:groupId/side-bets" element={<ProtectedRoute><SideBetsDrillIn /></ProtectedRoute>} />
    <Route path="/group/:groupId/manage" element={<ProtectedRoute><ManageDrillIn /></ProtectedRoute>} />
    <Route path="/how-it-works" element={<HowItWorks />} />
    <Route path="/tip-jar" element={<TipJar />} />
    <Route path="/about" element={<About />} />
    <Route path="/faq" element={<FaqPage />} />
    <Route path="/feedback" element={FEEDBACK_ENABLED ? <FeedbackPage /> : <Navigate to="/" replace />} />
    <Route path="/join/:inviteCode" element={<PublicOrProtected><JoinGroup /></PublicOrProtected>} />
    <Route path="/join" element={<ProtectedRoute><JoinGroup /></ProtectedRoute>} />
    <Route path="/bracket-leaderboard" element={<ProtectedRoute><GlobalLeaderboardPage /></ProtectedRoute>} />
    <Route path="/leaderboard" element={<Navigate to="/bracket-leaderboard" replace />} />
    <Route path="/bracket/compare/:userId1/:userId2" element={<ProtectedRoute><BracketComparePage /></ProtectedRoute>} />
    <Route path="/bracket" element={<ProtectedRoute><BracketPage /></ProtectedRoute>} />
    <Route path="/bracket/:userId" element={<ProtectedRoute><BracketPage /></ProtectedRoute>} />
    <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
    <Route path="/admin/analytics" element={<ProtectedRoute><AdminAnalytics /></ProtectedRoute>} />
    <Route path="*" element={<NotFound />} />
  </Routes>
);

const DevModeBadge = () => {
  const devMode = useDevMode();
  if (!devMode) return null;
  return (
    <div className="fixed bottom-4 left-4 z-[9999] bg-yellow-500 text-black text-xs font-bold px-3 py-1.5 rounded-full shadow-lg select-none pointer-events-none">
      DEV MODE
    </div>
  );
};

const App = () => (
  <SentryErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <PwaUpdatePrompt />
        <BrowserRouter>
          <AuthProvider>
            <AppRoutes />
            <AddToHomeScreen />
            <DevModeBadge />
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </SentryErrorBoundary>
);

export default App;
